import { useLocationContacts } from '@hakimo-ui/hakimo/data-access';
import { LocationContact } from '@hakimo-ui/hakimo/types';
import { useUser } from '@hakimo-ui/hakimo/util';
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildReportEmailBody } from '../email-utils';

const DEFAULT_BCC = ['cs@hakimo.ai'];

interface Args {
  open: boolean;
  locationId: number | null;
  site: string | null;
  incidentStartTime: string;
  description: string;
  subjectId?: string | number | null;
  initialAttachment?: File | null;
  resetOnOpen: boolean;
}

/**
 * Compose-form state, seeded when the modal opens.
 *
 * The modal stays mounted across the compose - preview - compose round
 * trip, so opening is an effect rather than a mount; `resetOnOpen` is
 * how the caller says which of the two it is.
 */
export function useEmailComposeState({
  open,
  locationId,
  site,
  incidentStartTime,
  description,
  subjectId,
  initialAttachment,
  resetOnOpen,
}: Args) {
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>(DEFAULT_BCC);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const prevOpenRef = useRef(false);
  const seededToRef = useRef(false);

  const user = useUser();
  const { data: contacts, isFetched: contactsFetched } = useLocationContacts(
    locationId || 0,
    open
  );

  const spocs = useMemo(
    () => (contacts ?? []).filter((c: LocationContact) => c.tag === 'SPOC'),
    [contacts]
  );

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (justOpened && resetOnOpen) {
      seededToRef.current = false;
      setToEmails([]);
      setBccEmails(DEFAULT_BCC);
      setAttachments(initialAttachment ? [initialAttachment] : []);
      setBody(
        buildReportEmailBody(site, incidentStartTime, description, user.name)
      );
      const idSuffix = subjectId ? `_${subjectId}` : '';
      setSubject(`IC-Incident-Report-(${site || 'Unknown Site'}${idSuffix})`);
    }
    // Seeding is one-shot per open, so it closes as soon as the contacts
    // query settles — an empty result included — and never overwrites a
    // recipient the operator typed while it was in flight.
    if (open && !seededToRef.current && contactsFetched) {
      seededToRef.current = true;
      const emails = spocs.map((s) => s.email).filter(Boolean);
      if (emails.length) {
        setToEmails((prev) => (prev.length ? prev : emails));
      }
    }
  }, [
    open,
    spocs,
    contactsFetched,
    site,
    incidentStartTime,
    description,
    subjectId,
    user.name,
    initialAttachment,
    resetOnOpen,
  ]);

  const reset = () => {
    setToEmails([]);
    setBccEmails(DEFAULT_BCC);
    setSubject('');
    setBody('');
    setAttachments([]);
  };

  return {
    toEmails,
    setToEmails,
    bccEmails,
    setBccEmails,
    subject,
    setSubject,
    body,
    setBody,
    attachments,
    setAttachments,
    reset,
  };
}
